'use client';

import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';

import { cn } from '../../helpers/utils';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
	<AccordionPrimitive.Item ref={ref} className={cn('border-b', className)} {...props} />
));
AccordionItem.displayName = 'AccordionItem';

interface AccordionTriggerProps extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> {
	className?: string;
	title?: string;
	icon?: React.ReactNode;
	rightHeaderContent?: React.ReactNode;
	leftHeaderContent?: React.ReactNode;
}

const AccordionTrigger = React.forwardRef<React.ElementRef<typeof AccordionPrimitive.Trigger>, AccordionTriggerProps>(
	(
		{ className, title, icon, rightHeaderContent: rightContent, leftHeaderContent: leftContent, children, ...props },
		ref,
	) => (
		<AccordionPrimitive.Header className="w-full relative">
			<AccordionPrimitive.Trigger
				ref={ref}
				className={cn(
					'py-4 w-full font-medium transition-all hover:underline [&[data-state=open]>div>svg]:rotate-180',
					className,
				)}
				{...props}
			>
				<div className="flex w-full items-start gap-2">
					<ChevronDown className="h-4 w-8 shrink-0 transition-transform duration-200 mt-0.5" />
					{icon && <span className="mr-2 shrink-0 mt-0.5">{icon}</span>}
					<span className="min-w-0 flex-1 whitespace-normal break-words text-left">{title}</span>
					{leftContent && <div className="ml-2 shrink-0">{leftContent}</div>}
					{rightContent && <div className="ml-auto shrink-0 mt-0.5">{rightContent}</div>}
				</div>
				<div className="pl-8 flex items-center w-full text-left">{children}</div>
			</AccordionPrimitive.Trigger>
		</AccordionPrimitive.Header>
	),
);
AccordionTrigger.displayName = 'AccordionTrigger';

const AccordionContent = React.forwardRef<
	React.ElementRef<typeof AccordionPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
	<AccordionPrimitive.Content
		ref={ref}
		className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
		{...props}
	>
		<div className={cn('pb-4 pt-0', className)}>{children}</div>
	</AccordionPrimitive.Content>
));
AccordionContent.displayName = 'AccordionContent';

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };

